/* eslint no-unused-expressions: [2, { allowShortCircuit: true, allowTernary: true }] */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Paginator from 'paginator';
import cx from 'classnames';
import Page from './Page';
import MemberStore from '../../stores/MemberStore';

export default class Pagination extends Component {
  buildPages(biggestActivePage) {
    const pages = [];
    const navigation = [];

    const {
      itemsCountPerPage,
      pageRangeDisplayed,
      activePage,
      prevPageText,
      nextPageText,
      firstPageText,
      lastPageText,
      totalItemsCount,
      onChange,
      activeClass,
      itemClass,
      activeLinkClass,
      disabledClass,
      hideDisabled,
      hideNavigation,
      linkClass,
      linkClassFirst,
      linkClassPrev,
      linkClassNext,
      linkClassLast,
    } = this.props;

    const pagInfo = new Paginator(itemsCountPerPage, pageRangeDisplayed)
      .build(totalItemsCount, activePage);

    // If last page is smaller than 5, but we are sure, that there are 5 or more pages,
    // set that last page to 5, so 5 pages will be shown in page list, not, for example, just two.
    // ("1 2 3 4 5 ... 10" instead of "1 2 ... 10").
    // ("1 2 3 4 5" instead of "1 2 ... 5").
    if (pagInfo.last_page < 10 && biggestActivePage >= 10) {
      pagInfo.last_page = 10;
    }

    // If pages are not starting with page 1, add that page 1 to page list
    if (pagInfo.first_page !== 1) {
      pages.push(<Page
        isActive={activePage === 1}
        key={1}
        pageNumber={1}
        pageText="1"
        onClick={onChange}
        itemClass={itemClass}
        linkClass={linkClass}
        activeClass={activeClass}
        activeLinkClass={activeLinkClass}
      />);
      if (pagInfo.first_page !== 2) {
        // If first page in list is 3, there is added page 1,
        // and gap between 1 and 3 is just one page,
        // and in this case better add page number (2), not '...'
        if (pagInfo.first_page === 3) {
          pages.push(<Page
            isActive={activePage === 2}
            key={2}
            pageNumber={2}
            pageText="2"
            onClick={onChange}
            itemClass={itemClass}
            linkClass={linkClass}
            activeClass={activeClass}
            activeLinkClass={activeLinkClass}
          />);
        } else {
          // If gap between page 1 and current page
          // is not just one page (first page in list is not 3), add '...'
          pages.push(<span key="first-more-gap" className="pagination-more mrf">...</span>);
        }
      }
    }

    for (let i = pagInfo.first_page; i <= pagInfo.last_page; i += 1) {
      pages.push(<Page
        isActive={i === activePage}
        key={i}
        pageNumber={i}
        pageText={`${i}`}
        onClick={onChange}
        itemClass={itemClass}
        linkClass={linkClass}
        activeClass={activeClass}
        activeLinkClass={activeLinkClass}
      />);
    }

    if (!hideDisabled && pagInfo.has_previous_page) {
      navigation.push(<Page
        key={`prev${pagInfo.previous_page}`}
        pageNumber={pagInfo.previous_page}
        onClick={onChange}
        pageText={prevPageText}
        isDisabled={!pagInfo.has_previous_page}
        itemClass={itemClass}
        linkClass={cx(linkClass, linkClassPrev)}
        disabledClass={disabledClass}
      />);
    }

    if ((!hideDisabled && pagInfo.has_previous_page) || !hideNavigation) {
      navigation.push(<Page
        key="first"
        pageNumber={1}
        onClick={onChange}
        pageText={firstPageText}
        isDisabled={!pagInfo.has_previous_page}
        itemClass={itemClass}
        linkClass={cx(linkClass, linkClassFirst)}
        disabledClass={disabledClass}
      />);
    }

    if (!hideDisabled && pagInfo.has_next_page) {
      navigation.push(<Page
        key={`next${pagInfo.next_page}`}
        pageNumber={pagInfo.next_page}
        onClick={onChange}
        pageText={nextPageText}
        isDisabled={!pagInfo.has_next_page}
        itemClass={itemClass}
        linkClass={cx(linkClass, linkClassNext)}
        disabledClass={disabledClass}
      />);
    }

    if ((!hideDisabled && pagInfo.has_next_page) || !hideNavigation) {
      navigation.push(<Page
        key="last"
        pageNumber={pagInfo.total_pages}
        onClick={onChange}
        pageText={lastPageText}
        isDisabled={pagInfo.current_page === pagInfo.total_pages}
        itemClass={itemClass}
        linkClass={cx(linkClass, linkClassLast)}
        disabledClass={disabledClass}
      />);
    }

    // If biggest active page is bigger than biggest page in current page list,
    // add option to quickly jump back to that biggest active page
    if (biggestActivePage > pagInfo.last_page) {
      const difference = biggestActivePage - pagInfo.last_page;
      if (difference > 1) {
        if (difference === 2) {
          // If page gap is just one page, then better add that page number, not '...'
          const extraPage = biggestActivePage - 1;
          pages.push(<Page
            isActive={extraPage === activePage}
            key={extraPage}
            pageNumber={extraPage}
            pageText={`${extraPage}`}
            onClick={onChange}
            itemClass={itemClass}
            linkClass={linkClass}
            activeClass={activeClass}
            activeLinkClass={activeLinkClass}
          />);
        } else {
          // If gap is bigger than one, add '...'
          pages.push(<span key="last-more-gap" className="pagination-more mrf">...</span>);
        }
      }

      // Add last active page number, to be able to quickly jump back to last active page.
      // If, for example, have jumped from page 30 to page 1,
      // to be able to jump back to that page 30.
      pages.push(<Page
        isActive={biggestActivePage === activePage}
        key={biggestActivePage}
        pageNumber={biggestActivePage}
        pageText={`${biggestActivePage}`}
        onClick={onChange}
        itemClass={itemClass}
        linkClass={linkClass}
        activeClass={activeClass}
        activeLinkClass={activeLinkClass}
      />);
    }

    return [pages, navigation];
  }

  render() {
    const { innerClass, section } = this.props;

    const biggestActivePage = MemberStore.getBiggestActivePage(section);
    const pages = this.buildPages(biggestActivePage);

    const morePages = MemberStore.getMorePages(section);

    return (
      <div className={innerClass}>
        <div className="pagination-numbers">
          {pages[0]}
          <span className="pagination-more">
            {morePages ? '...' : ''}
          </span>
        </div>
        <div className="paginator-prevNextWrap">
          {pages[1]}
        </div>
      </div>
    );
  }
}

Pagination.propTypes = {
  totalItemsCount: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  activePage: PropTypes.number,
  itemsCountPerPage: PropTypes.number,
  pageRangeDisplayed: PropTypes.number,
  prevPageText: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
  ]),
  nextPageText: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
  ]),
  lastPageText: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
  ]),
  firstPageText: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
  ]),
  disabledClass: PropTypes.string,
  hideDisabled: PropTypes.bool,
  hideNavigation: PropTypes.bool,
  innerClass: PropTypes.string,
  itemClass: PropTypes.string,
  linkClass: PropTypes.string,
  activeClass: PropTypes.string,
  activeLinkClass: PropTypes.string,
  linkClassFirst: PropTypes.string,
  linkClassPrev: PropTypes.string,
  linkClassNext: PropTypes.string,
  linkClassLast: PropTypes.string,
  section: PropTypes.string,
};

Pagination.defaultProps = {
  itemsCountPerPage: 10,
  pageRangeDisplayed: 10,
  activePage: 1,
  prevPageText: '⟨',
  firstPageText: '«',
  nextPageText: '⟩',
  lastPageText: '»',
  innerClass: 'pagination',
  itemClass: undefined,
  linkClass: undefined,
  activeLinkClass: undefined,
  linkClassLast: undefined,
  linkClassNext: undefined,
  linkClassPrev: undefined,
  linkClassFirst: undefined,
  activeClass: undefined,
  hideNavigation: false,
  hideDisabled: false,
  disabledClass: undefined,
  section: 'default',
};
